import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, ButtonGroup } from "@components/Form";
import { CustomModal } from "@components/Modals";
import { useJournalTypes } from "@hooks/useJournalTypes";
import clsx from "clsx";
import styles from "./JournalFolderSelectionModal.module.scss";

const JournalFolderSelectionModal = ({
  isOpen,
  onClose,
  onContinue,
  journals = [],
  className = "",
}) => {
  const { t, i18n } = useTranslation();
  const { journalTypes: allJournalTypes, isLoading } = useJournalTypes({
    enabled: isOpen && journals.length === 0,
  });
  const [selectedJournalFolder, setSelectedJournalFolder] = useState(null);

  const journalList = journals.length > 0 ? journals : allJournalTypes;

  const handleClose = () => {
    setSelectedJournalFolder(null);
    onClose();
  };

  const handleContinue = () => {
    if (selectedJournalFolder) {
      onContinue(selectedJournalFolder);
      handleClose();
    }
  };

  return (
    <CustomModal
      openModal={isOpen}
      setOpenModal={handleClose}
      className={clsx(styles.modal, "bg-milk rounded-xl overflow-auto")}
      style={{ padding: "0px !important" }}
      hideCloseButton
    >
      <div className="overflow-hidden">
        <div className="flex flex-col justify-center text-center px-12">
          <h4 className="mt-1 pb-1 pt-3 text-3xl text-dawn-gray font-bold-poppins">
            {t("customReports.journalAccessScreen.chooseJournal")}
          </h4>
        </div>

        <div className="flex flex-col justify-center text-center">
          <h5 className="mt-1 pb-1 pt-3 px-4 text-2xl text-dawn-gray font-medium-poppins">
            {t("customReports.journalAccessScreen.description")}
          </h5>
        </div>

        <div className="flex flex-1 flex-col px-0 my-3 gap-6 justify-center items-center">
          {journalList?.length ? (
            <div className="ml-4 my-2 flex flex-column flex-wrap w-full">
              <ButtonGroup
                className="flex flex-row flex-wrap gap-2 justify-center w-full"
                variant="report"
                variantSelected="reportSelected"
                options={journalList.map((folder) => ({
                  label:
                    i18n.language === "sv" ? folder.nameSw : folder.nameEng,
                  value: folder.id,
                }))}
                selectedOptions={
                  selectedJournalFolder
                    ? [
                        {
                          label:
                            i18n.language === "sv"
                              ? selectedJournalFolder.nameSw
                              : selectedJournalFolder.nameEng,
                          value: selectedJournalFolder.id,
                        },
                      ]
                    : []
                }
                onChange={(e) => {
                  const selectedFolder = journalList.find(
                    (folder) => folder.id === e.value,
                  );
                  setSelectedJournalFolder(selectedFolder || null);
                }}
              />
            </div>
          ) : null}

          <div className="flex flex-wrap justify-center gap-2 mt-5 mb-7">
            <Button
              onClick={handleContinue}
              disabled={!selectedJournalFolder || isLoading}
              className="rounded-full flex justify-center items-center px-16 mx-auto py-3 text-white text-h-4 completeButton disabled:opacity-50"
            >
              <div className="flex justify-center items-center">
                {t("common.continue")}
              </div>
            </Button>
            <Button
              variant="negative"
              className="rounded-full flex justify-center items-center px-16 py-3"
              onClick={handleClose}
            >
              <span className="text-primary font-medium-poppins tracking-tight">
                {t("common.cancel")}
              </span>
            </Button>
          </div>
        </div>
      </div>
    </CustomModal>
  );
};

export default JournalFolderSelectionModal;
